import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import { PROFILE_IMAGES } from "../../@shared/utils/profileImages";

const getDialogPalette = (theme) => ({
  primary: theme?.palette?.primary?.main || "#60a5fa",
  secondaryMain: theme?.palette?.secondary?.main || "#7c3aed",
  secondaryDark: theme?.palette?.secondary?.dark || "#5b21b6",
  tirciaryDark: theme?.palette?.tirciary?.dark || "#0f172a",
  tirciaryMain: theme?.palette?.tirciary?.main || "#1e293b",
  tirciaryLight: theme?.palette?.tirciary?.light || "#93c5fd",
});

const normalizeSpeakerName = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const SPEAKER_PORTRAIT_LOOKUP = PROFILE_IMAGES.reduce((lookup, entry) => {
  const key = normalizeSpeakerName(entry?.name);
  if (key && entry?.img && !lookup[key]) {
    lookup[key] = entry.img;
  }
  return lookup;
}, {});

const resolveSpeakerPortrait = (speaker = {}) => {
  if (speaker?.portrait) return speaker.portrait;
  return SPEAKER_PORTRAIT_LOOKUP[normalizeSpeakerName(speaker?.name)] || null;
};

const resolveSpeakerPortraitData = (speaker = {}) => ({
  portrait: resolveSpeakerPortrait(speaker),
  portraitSprite: speaker?.portraitSprite || null,
});

function useTypewriter(text = "", speed = 18, instant = false) {
  const [out, setOut] = useState(instant ? text : "");
  const [done, setDone] = useState(instant);

  useEffect(() => {
    if (instant) {
      setOut(text);
      setDone(true);
      return;
    }

    setOut("");
    setDone(false);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setOut(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(id);
        setDone(true);
      }
    }, speed);

    return () => clearInterval(id);
  }, [text, speed, instant]);

  return {
    out,
    done,
    skip: () => {
      setOut(text);
      setDone(true);
    },
  };
}

export const DialogBox = ({
  theme,
  speaker = { name: "???", portrait: null },
  text,
  onNext,
  canContinue = true,
  showInstant = false,
}) => {
  const palette = getDialogPalette(theme);
  const { out, done, skip } = useTypewriter(text, 14, showInstant);
  const handleAdvance = useCallback(
    () => (done ? onNext?.() : skip()),
    [done, onNext, skip],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (["Enter", " ", "ArrowRight", "z", "Z"].includes(e.key)) {
        e.preventDefault();
        handleAdvance();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [done, handleAdvance]);

  return (
    <Box
      data-testid="dialog-box"
      onClick={handleAdvance}
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 12,
        mx: "auto",
        width: { xs: "96%", sm: "86%", md: "70%" },
        pointerEvents: "auto",
        border: "3px solid black",
        borderRadius: 2,
        background: palette.tirciaryDark,
        boxShadow: "0 4px 0 rgba(0,0,0,.4)",
      }}
    >
      <Box
        sx={{
          border: `1px solid ${palette.tirciaryLight}`,
          borderRadius: 1.5,
          p: 0.5,
        }}
      >
        <Grid
          container
          wrap="nowrap"
          columnGap={1.5}
          sx={{
            border: `1px solid ${palette.tirciaryLight}`,
            borderRadius: 1.25,
            p: 1,
            alignItems: "flex-start",
            background: palette.tirciaryMain,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "inline-flex",
                border: "3px solid black",
                borderRadius: 1,
                px: 1,
                py: 0.75,
                mb: 0.75,
                background: palette.tirciaryDark,
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: 900, color: palette.primary, lineHeight: 1 }}
              >
                {speaker?.name ?? "---"}
              </Typography>
            </Box>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: 16, md: 18 },
                lineHeight: 1.35,
                color: "#ffffff",
                pr: 6,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {out}
            </Typography>
          </Box>

          {canContinue && done ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <KeyboardArrowRightRoundedIcon
                fontSize="large"
                style={{ color: "#ffffff" }}
              />
            </Box>
          ) : null}
        </Grid>
      </Box>
    </Box>
  );
};

export const DialogChoices = ({
  theme,
  choices = [],
  onChoose,
  isMobile = false,
}) => {
  const palette = getDialogPalette(theme);

  if (!choices?.length) return null;

  return (
    <Stack
      direction={isMobile ? "column" : "row"}
      spacing={1}
      sx={{
        position: "absolute",
        right: 5,
        bottom: 96,
        mx: "auto",
        width: { xs: "50%", sm: "86%", md: "70%" },
        justifyContent: "center",
        flexWrap: "wrap",
        pointerEvents: "auto",
      }}
    >
      {choices.map((c, i) => (
        <Button
          key={`${c.id ?? i}-${c.label}`}
          onClick={() => onChoose?.(c)}
          variant="contained"
          sx={{
            border: "3px solid black",
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 800,
            background:
              i === choices.length - 1
                ? palette.tirciaryMain
                : palette.secondaryMain,
            "&:hover": {
              background:
                i === choices.length - 1
                  ? palette.tirciaryDark
                  : palette.secondaryDark,
            },
          }}
        >
          {c.label}
        </Button>
      ))}
    </Stack>
  );
};

export const DialogBackdropPortrait = ({
  src,
  spriteSheet = null,
  name,
  side = "left",
  isMobile = false,
}) => {
  if (!src && !spriteSheet?.image) return null;

  const sharedSx = {
    position: "absolute",
    bottom: "10%",
    [side]: isMobile ? "-14%" : "14%",
    height: "55%",
    zIndex: 0,
    pointerEvents: "none",
  };

  if (!src && spriteSheet?.image) {
    const frame = Math.max(0, Number(spriteSheet.frame || 0));
    const columns = Math.max(1, Number(spriteSheet.columns || 1));
    const rows = Math.max(1, Number(spriteSheet.rows || 1));
    const columnIndex = frame % columns;
    const rowIndex = Math.floor(frame / columns);
    const positionX = columns === 1 ? 0 : (columnIndex / (columns - 1)) * 100;
    const positionY = rows === 1 ? 0 : (rowIndex / (rows - 1)) * 100;

    return (
      <Box
        aria-label={name}
        role="img"
        sx={{
          ...sharedSx,
          aspectRatio: `${spriteSheet.frameWidth || 1}/${spriteSheet.frameHeight || 1}`,
          width: "auto",
          backgroundImage: `url(${spriteSheet.image})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: `${columns * 100}% ${rows * 100}%`,
          backgroundPosition: `${positionX}% ${positionY}%`,
          imageRendering: "pixelated",
        }}
      />
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt={name}
      sx={{
        ...sharedSx,
        aspectRatio: "3/4",
        objectFit: "scale-down",
        objectPosition: "bottom",
      }}
    />
  );
};

export default function DialogHUD({
  theme,
  open,
  script = [],
  onClose,
  onLineChange,
  showInstant = false,
  backdrop = true,
  captureBackdrop = true,
}) {
  const [i, setI] = useState(0);
  const node = script[i];
  const resolvedSpeaker = node
    ? (() => {
        const portraitData = resolveSpeakerPortraitData(node.speaker);
        return {
          ...node.speaker,
          ...portraitData,
        };
      })()
    : null;

  useEffect(() => {
    if (!open) setI(0);
  }, [open]);

  useEffect(() => {
    if (!open || !node) return;
    onLineChange?.(node, i);
  }, [i, node, onLineChange, open]);

  const handleNext = () => {
    if (i < script.length - 1) setI(i + 1);
    else onClose?.();
  };

  const handleChoice = async (choice) => {
    if (typeof node?.onChoose === "function") await node.onChoose(choice);
    onClose?.(choice);
  };

  if (!open || !node) return null;

  return (
    <Box
      data-testid="dialog-hud"
      sx={{
        position: "absolute",
        inset: 0,
        bottom: 5,
        zIndex: 9999,
        bgcolor: backdrop ? "#00000080" : "transparent",
        pointerEvents: captureBackdrop ? "auto" : "none",
      }}
    >
      <DialogBackdropPortrait
        src={resolvedSpeaker?.portrait}
        spriteSheet={resolvedSpeaker?.portraitSprite}
        name={resolvedSpeaker?.name}
        side="left"
        isMobile={typeof window !== "undefined" && window.innerWidth < 900}
      />
      <DialogBox
        theme={theme}
        speaker={resolvedSpeaker}
        text={node.text}
        onNext={handleNext}
        canContinue={!node?.choices?.length}
        showInstant={showInstant}
      />
      <DialogChoices
        theme={theme}
        choices={node.choices}
        onChoose={handleChoice}
        isMobile={typeof window !== "undefined" && window.innerWidth < 900}
      />
    </Box>
  );
}
